import { create } from 'zustand';

import { authService } from '@/services/auth/auth.service';
import { registerSessionExpiredHandler } from '@/services/auth/session-manager';
import { tokenStorage } from '@/services/storage/token.storage';
import type { AuthUser, LoginCredentials, Role } from '@/types/auth';
import { decodeJwtPayload, isJwtExpired } from '@/utils/jwt';

interface AccessTokenClaims {
  sub: string;
  email: string;
  name?: string;
  roles: string[];
}

/**
 * The backend issues Spring Security-style authorities (e.g. "ROLE_ADMIN")
 * in the JWT's `roles` claim, prefixed - strip that prefix so it lines up
 * with the unprefixed `Role` values used everywhere else (permissions map,
 * user role management endpoints).
 */
function normalizeRole(role: string): Role {
  return role.replace(/^ROLE_/, '') as Role;
}

function userFromAccessToken(accessToken: string): AuthUser | null {
  const claims = decodeJwtPayload<AccessTokenClaims>(accessToken);
  if (!claims) return null;

  return {
    id: claims.sub,
    email: claims.email,
    name: claims.name ?? claims.email,
    roles: (claims.roles ?? []).map(normalizeRole),
  };
}

type AuthStatus = 'idle' | 'authenticated' | 'unauthenticated';

interface AuthState {
  status: AuthStatus;
  user: AuthUser | null;
  isLoading: boolean;
  error: string | null;

  initialize: () => void;
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => Promise<void>;
}

export const useAuthStore = create<AuthState>()((set) => ({
  status: 'idle',
  user: null,
  isLoading: false,
  error: null,

  initialize: () => {
    const accessToken = tokenStorage.getAccessToken();
    if (!accessToken || isJwtExpired(accessToken)) {
      tokenStorage.clearTokens();
      set({ status: 'unauthenticated', user: null });
      return;
    }

    set({ status: 'authenticated', user: userFromAccessToken(accessToken) });
  },

  login: async (credentials) => {
    set({ isLoading: true, error: null });
    try {
      const response = await authService.login(credentials);
      tokenStorage.setTokens(response);
      set({ status: 'authenticated', user: userFromAccessToken(response.accessToken), isLoading: false });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Login failed';
      set({ isLoading: false, error: message });
      throw error;
    }
  },

  logout: async () => {
    const refreshToken = tokenStorage.getRefreshToken();
    tokenStorage.clearTokens();
    set({ status: 'unauthenticated', user: null });

    if (refreshToken) {
      try {
        await authService.logout(refreshToken);
      } catch {
        // Best-effort: the client-side session is already cleared regardless.
      }
    }
  },
}));

registerSessionExpiredHandler(() => {
  useAuthStore.setState({
    status: 'unauthenticated',
    user: null,
    error: 'Your session has expired. Please sign in again.',
  });
});
